import React from "react";
import { withStyles, Divider, Grid } from "@material-ui/core";
import { compose } from "recompose";
import { withSnackbar } from "notistack";
import { inject, observer } from "mobx-react";
import styles from "./styles";
import CustomRadioGroup from "../GroupMsg/Components/CustomRadioGroup";
import {
  postData,
  getDataWithDeduplication,
} from "../../../../../api/componentAPIs";
import appConstants from "../../../../../appConstants";
import { sortBy } from "lodash";
import MESSAGES from "../../../../../appMessages";
import { RMAXLogger } from "../../../../../services/LoggerService/RMAXLogger";
import Events from "../../../../../utilities/EventEmitter";
import Bobtail from "./DispatchBobtail";
import DispatchTrailer from "./DispatchTrailer";
import PreDispatchTrailerPanel from "./PreDispatchTrailerPanel";
import { PanelHeader, DispatchError } from "./components/index";
import {
  getDriverError,
  getDriverErrorArgs,
  getEquipmentStatusesVisibleForDispatch,
  getEventFailureMessage,
} from './utils';
import  DispatchInProgress from "./DispatchInProgress";
import WithDrawer from "../../Common/Drawer/WithDrawer"
import EquipmentDefects from "./EquipmentDefects";
import { fetchDriverImage, getEquipmentSuffix } from "../../utils/utils";
import SafetyData from "./SafetyData";
import { withAppConfig } from "../../../../../appConfigContext";
import { sanitizeInput } from "../../../../../utilities/dataUtils";
import { ErrorBoundary } from "../../../../../ErrorBoundary";
import constants from "./constants";
import { getRouteNameMaxLengthFromConfig, sortRouteOptionsByValue } from "./routeNameUtils";
import {
  findDriverById,
  getPreSelectedTrailer,
  buildIdealStartTimesByTrailer,
  getIdealStartTimesForTrailers,
  updateIdealStartTimesByTrailerAtIndex,
  getDispatchTrailerKey,
  resolvePreDispatchIdealStartSync,
} from "./preDispatchTrailerUtils";
import { isDriverReadyForDispatch } from "../utils/index";

@inject("DispatchStore")
@observer
class DispatchPanel extends React.Component {
  constructor(props) {
    super(props);
    const initialPreDispatchTrailers = props?.selectedTrailer ?? [];
    this.state = {
      selectedDispatchType: appConstants.DISPATCH_TYPE.TRAILER,
      isDataLoaded: false,
      dispatchError: null,
      driver: props.driver ?? {},
      dispatchInProgress: false,
      dispatchingDelayed: false,
      showEquipementDefect: null,
      driversImage: {},
      isDriverSafetyOk: false,
      preDispatchTrailers: initialPreDispatchTrailers,
      preDispatchTractor: props?.selectedTractor ?? constants.DEFAULT_TRACTOR,
      preDispatchIdealStartTimesByTrailer: props.isPreDispatch ? buildIdealStartTimesByTrailer({
        preDispatchIdealStartTimesByTrailer: {},
        trailerDetails: initialPreDispatchTrailers,
      }) : {},
      preDispatchTrailerDetailsForIdealStart: null,
    };

    this.trailerDetailsList = [];
    this.routeList = this.prepareRouteList();
    this.routeNameConfig = {
      maxLength: getRouteNameMaxLengthFromConfig({ configJSON: props.configJSON }),
    };
    this.tractorList = [];
    this.dollyList = [];
    this.trailerList = [];
    this.timer = null;
    this.container = React.createRef();
    this.dispatchedDriver = null;
    this.printDrData = null;
  }
  componentDidMount() {
    this.fetchAllEquipments();
    Events.on("DriverDispatched", this.checkDriverDispatchedStatus);
    Events.on("RefreshEquipmentData", this.refreshEquipmentData);
    RMAXLogger.info("Dispatch_Panel", { action: "Viewed" });
    this.setDriverImage(this.state.driver.driverNumber);
    this.syncPreselectedDriverErrorAndSafety();
  }

  // When panel is opened from drivers grid entry, driver is pre-selected but driverError, is not computed in constructor. Sync validation error and safety data into state.
  syncPreselectedDriverErrorAndSafety = () => {
    if (!this.props.driver?.driverId) {
      return;
    }
    const driverData = this.getSelectedDriverData(this.props.driver.driverId);
    const driverError = getDriverError({
      driverData,
      driverStatusConfig: this.props.DispatchStore.staticClientData?.clientCodes?.driverStatusConfig ?? [],
      terminalOffset: this.props.terminalOffset,
      isDriverNotYetDispatched: this.isDriverNotYetDispatched(this.props.driver.driverId)
    });
    this.setState((prevState) => ({
      driver: {
        ...prevState.driver,
        driverError,
        safetyData: this.normalizeSafetyData(driverData.safetyData)
      }
    }));
  }

  componentWillUnmount() {
    Events.removeListener("DriverDispatched", this.checkDriverDispatchedStatus);
    Events.removeListener("RefreshEquipmentData", this.refreshEquipmentData);
    
    // Clear timeout timer to prevent memory leak
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    
    // Clear large data arrays to free memory
    this.trailerDetailsList = null;
    this.tractorList = null;
    this.dollyList = null;
    this.trailerList = null;
    this.routeList = null;
    this.routeNameConfig = null;
    
    // Clear driver image cache
    this.clearDriverImageCache();
  }

  isDriverEligibleForNormalDispatch = (driverId = this.state.driver?.driverId) => {
    if (!driverId) {
      return false;
    }
    const driverData = this.getSelectedDriverData(driverId);
    return isDriverReadyForDispatch({
      driverClockedInTime: driverData.clockedInTime,
      isCompanyDriver: driverData.isCompanyDriver,
      terminalOffset: this.props.terminalOffset,
    });
  };

  getDriverErrorArgsInput = (driverId, driverDataFromStore = null) => ({
    driverId,
    driverData: this.getSelectedDriverData(driverId),
    driverStatusConfig: this.props.DispatchStore.staticClientData?.clientCodes?.driverStatusConfig ?? [],
    terminalOffset: this.props.terminalOffset,
    isDriverNotYetDispatched: this.isDriverNotYetDispatched(driverId),
    isPreDispatch: this.props.isPreDispatch,
    isPreSelected: this.isDriverPreSelected(),
    dispatchedDrivers: this.props.DispatchStore.dispatchedDrivers,
    maxAllowedTrailerCount: this.props.DispatchStore?.maxAllowedTrailerCount ?? 0,
    driverDataFromStore,
    preSelectedTrailer: this.props.selectedTrailer,
  });

  componentDidUpdate(prevProps, prevState) {
    if (!this.props.driver && this.state.driver.driverId !== prevState.driver?.driverId) {
      const driverError = getDriverError(
        getDriverErrorArgs(this.getDriverErrorArgsInput(this.state.driver.driverId))
      );
      if (this.state.driver.driverError?.error !== driverError.error) {
        this.setState((prevState) => ({
          driver: {
            ...prevState.driver,
            driverError,
          },
        }));
      }
    }
  }

  onDispatchProgressTimeOut = () => {
    this.timer = setTimeout(() => {
      this.setState({ dispatchingDelayed: true });
    }, appConstants.ONE_MINUTE_TIME_OUT);
  };

  emitPrintPhysicalDrAfterDispatch = (routeIds) => {
    if (!this.props.configJSON?.enablePhysicalDrPrintPreview) {
      this.printDrData = null;
      return;
    }
    // routeIds change during dispatch, so use the fresh routeIds from the DriverDispatched SignalR event.
    // SignalR sends GUIDs in lowercase (same as driverId above); normalize to uppercase to match the
    // casing GeneratePhysicalDR expects, otherwise the backend lookup misses and returns an empty blobSasUrl.
    const printRouteIds = (routeIds ?? [])
      .filter(Boolean)
      .map((routeId) => routeId.toUpperCase());
    if (this.printDrData?.printDrAfterDispatch && printRouteIds.length > 0) {
      Events.emit("PrintPhysicalDRAfterDispatch", {
        terminalCode: this.props.terminalCode,
        routeIds: printRouteIds,
      });
    }
    this.printDrData = null;
  };

  checkDriverDispatchedStatus = (data) => {
    const { success, message, driverId, routeIds } = data;
    if (this.dispatchedDriver?.toUpperCase() !== driverId?.toUpperCase()) {
      // if dispatch update event is not for driver dispatched by current user skip showing success/failure notification
      return;
    }
    const { driverName } = this.state.driver;
    if (success) {
      this.props.enqueueSnackbar(`${driverName} dispatched successfully. `, {
        variant: "success",
      });
      this.emitPrintPhysicalDrAfterDispatch(routeIds);
      this.props.onClose();
    } else {
      this.props.enqueueSnackbar(`Dispatching ${driverName} failed`, {
        variant: "error",
      });
      this.dispatchedDriver = null;
      this.printDrData = null;
      this.setState({
        dispatchInProgress: false,
        dispatchingDelayed: false,
        dispatchError: message?.error ? JSON.stringify(message.error): getEventFailureMessage(),
      });
    }
    clearTimeout(this.timer);
  };


  refreshEquipmentData = (equipmentType , data) => {
    const {eventType, success, action, showNotification, ...equipmentData } = data;
    let isMatchFound = false;
    if (equipmentType === appConstants.EQUIPMENT_TYPES.Trailer) {
      this.trailerDetailsList = this.trailerDetailsList.map(trailer => {
        if (trailer.trailerId === equipmentData.trailerId) {
          isMatchFound = true;
          return {
            ...trailer,
            ...equipmentData
          };
        }
        return trailer;
      });
      this.generateTrailersOption(this.trailerDetailsList);
    }
    else if (equipmentType === appConstants.EQUIPMENT_TYPES.Dolly) {
      this.dollyList = this.dollyList.map(dolly => {
        if (dolly.dollyId === equipmentData.dollyId) {
           isMatchFound = true;
          return {
            ...dolly,
            ...equipmentData
          };
        }
        return dolly; 
      });
      this.generateDollyOption(this.dollyList);
    }
    else if (equipmentType === appConstants.EQUIPMENT_TYPES.Tractor) {
      this.tractorList = this.tractorList.map(tractor => {
        if (tractor.tractorId === equipmentData.tractorId) {
           isMatchFound = true;
          return {
            ...tractor,
            ...equipmentData
          };
        }
        return tractor;
      });
      this.generateTractorOption(this.tractorList);
    }
    if(!isMatchFound) {
      this.fetchEquipmentByType(equipmentType);
    } else {
      this.setState({isDataLoaded: true});
    }
  }

  fetchEquipmentByType = (equipmentType) => {
    getDataWithDeduplication(
      "GetAllEquipment",
      `terminalCode=${this.props.userProfile.terminal.code}&equipmentType=${equipmentType}`
    ).then((allEquipmentRes) => {
      if (allEquipmentRes?.status === 200) {
        this.generateTrailersOption(allEquipmentRes.data.trailer);
        this.setState({isDataLoaded: true});
      } else {
        this.generateTrailersOption([]);
        this.handleError();
      }
    });
  };

  // If trailer route is DEFAULTROUTE, ignore that route and let the user select/create a different route.
  updateTrailersWithValidRoutes = (trailers) => {
    trailers.forEach((trailer) => {
      if (trailer.routeName === constants.ROUTE_NAME_DEFAULT) {
        trailer.routeName = null;
      }
    });
  };

  generateTrailersOption = (trailers) => {
    const { trailerStatusConfig=[] } = this.props.DispatchStore.staticClientData?.clientCodes ?? {};
    const trailerStatusInOrder = getEquipmentStatusesVisibleForDispatch(trailerStatusConfig);
    this.updateTrailersWithValidRoutes(trailers);
    // sort by trailernumber
    this.trailerDetailsList = sortBy(trailers, [
      (item) => trailerStatusInOrder.indexOf(item.trailerStatus),
      (item) => item.trailerNumber
    ]);
    this.trailerList = this.getSortedTrailerList(this.trailerDetailsList);
  };


  fetchAllEquipments = () => {
    const {terminalCode} = this.props;
    getDataWithDeduplication("GetAllEquipment", `terminalCode=${terminalCode}`).then((allEquipmentRes) => {
      if (allEquipmentRes.status === 200) {
        const allEquipment = allEquipmentRes?.data ?? [];
        this.generateDataOptions(allEquipment);
        this.setState({
          isDataLoaded: true,
        });
      } else {
        this.handleError();
      }
    });
  };

  sortByLabel = (optionsList = []) => {
    return sortBy(optionsList, [(item) => parseInt(item.label)]);
  };

  prepareRouteList = () => {
    const routeNamesForTerminal = this.props.DispatchStore.staticClientData?.clientCodes?.routeNamesForTerminal || [];
    return sortRouteOptionsByValue(
      routeNamesForTerminal.map((item) => ({
        label: item.routeName,
        value: item.routeName,
        mappedRouteName: item.mappedRouteName,
        labelSuffix: Boolean(item?.description)
          ? ` - ${item.description.trim()}`
          : "",
      }))
    );
  };

  generateDataOptions = (allEquipments) => {
    const {tractorStatusConfig=[] , dollyStatusConfig=[]} = this.props.DispatchStore.staticClientData?.clientCodes ?? {};

    const {trailer = [], dolly = [], tractor = [] } = allEquipments;
    this.tractorList = this.sortByLabel(
      tractor
        .filter((item) =>
          getEquipmentStatusesVisibleForDispatch(tractorStatusConfig).includes(
            item.tractorStatus
          ) && (this.props.isPreDispatch || !item.tractorNumber?.includes(constants.DEFAULT_TRACTOR))
        )
        .map(
          ({
            tractorNumber,
            tractorStatus,
            tractorScac,
            tractorId,
            isStraightTruck,
            defects,
          }) => ({
            label: tractorNumber,
            value: tractorId,
            labelSuffix: getEquipmentSuffix({
              equipmentStatus: tractorStatus,
              equipmentScac: tractorScac,
              showTrailerScac: this.props.configJSON.showTrailerScac,
            }),
            tractorStatus,
            isStraightTruck,
            ...(tractorScac && {tractorScac}),
            defects,
          })
        )
    );
    
    this.dollyList = this.sortByLabel(
      dolly.filter(
        (item) =>
          getEquipmentStatusesVisibleForDispatch(dollyStatusConfig).includes(item.dollyStatus) &&
          (this.props.isPreDispatch || !item.dollyNumber?.includes(constants.DEFAULT_DOLLY))
      ).map(
        ({ dollyNumber, dollyStatus, dollyId, defects, dollyScac }) => ({
          label: dollyNumber,
          value: dollyId,
          labelSuffix: getEquipmentSuffix({
            equipmentStatus: dollyStatus,
            equipmentScac: dollyScac,
            showTrailerScac: this.props.configJSON.showTrailerScac,
          }),
          dollyStatus,
          defects,
        })
      )
    );

    this.generateTrailersOption(trailer);
  };

  getDriversList = () => {
    return this.props.allAvailableDriverList.map(
      ({ driverName, driverNumber, driverId }) => ({
        label: driverName,
        value: driverId,
        labelSuffix: `#${driverNumber}`,
      })
    );
  }

  getSortedTrailerList = (trailer = []) => {
    if (!trailer.length) {
      return trailer;
    }

    const { trailerStatusConfig=[] } = this.props.DispatchStore.staticClientData?.clientCodes ?? {};
    const trailerStatusInOrder = getEquipmentStatusesVisibleForDispatch(trailerStatusConfig);

    // filter by allowed trailer status and sort by trailer status order
    return trailer
      .filter((item) => trailerStatusInOrder.includes(item.trailerStatus))
      .map(
        ({
          trailerNumber,
          trailerId,
          trailerStatus,
          trailerType,
          trailerScac,
          defects,
          routeName,
          numberOfProsLoaded
        }) => ({
          label: trailerNumber,
          value: trailerId,
          labelSuffix: getEquipmentSuffix({
            equipmentStatus: trailerStatus,
            equipmentScac: trailerScac,
            showTrailerScac: this.props.configJSON.showTrailerScac,
            prosLoadedInTrailer: numberOfProsLoaded,
          }),
          trailerStatus,
          trailerType,
          defects,
          routeName,
        })
      );
  };

  handleError = () => {
    this.props.enqueueSnackbar(MESSAGES.COMMON.LOADING_DATA, {
      variant: "error",
    });
    this.setState({
      isDataLoaded: true,
    });
  };

  handleRadioChange = (value) => {
    this.setState({
      selectedDispatchType: value,
      dispatchError: null,
    });
  };

  dispatchDriver = (data) => {
    const isPreDispatch = data?.isPreDispatch ?? this.props.isPreDispatch;
    const isPreSelected = this.isDriverPreSelected();
    const driverError = getDriverError({
      driverData: this.getSelectedDriverData(this.state.driver.driverId),
      driverStatusConfig: this.props.DispatchStore.staticClientData?.clientCodes?.driverStatusConfig ?? [],
      terminalOffset: this.props.terminalOffset,
      isDriverNotYetDispatched: this.isDriverNotYetDispatched(this.state.driver.driverId),
      isPreDispatch,
      isPreSelected,
    });
    if (driverError.error || !data) {
      this.setState((prevState) => ({
        driver: {
          ...prevState.driver,
          driverError: driverError,
        },
      }));
      return;
    }
    const { printDrAfterDispatch, routeIds: _routeIds, isPreDispatch: _submitIsPreDispatch, ...dispatchData } = data;
    this.printDrData = this.props.configJSON?.enablePhysicalDrPrintPreview
    ? { printDrAfterDispatch: Boolean(printDrAfterDispatch) }
    : null;
    const reqParams = {
      terminalCode: this.props.terminalCode,
      driverId: this.state.driver.driverId,
      eventDateTime: new Date(),
      isPreDispatch,
      ...dispatchData,
      notes: sanitizeInput(dispatchData.notes),
    };
    this.setState(
      {
        dispatchInProgress: true,
        dispatchError: null,
      },
      this.onDispatchProgressTimeOut
    );
    RMAXLogger.info("Dispatch_Panel", {
      event: "Dispatch_Driver",
      driverId: reqParams.driverId,
      terminalCode: reqParams.terminalCode,
      isPreDispatch: reqParams.isPreDispatch
    });
    this.dispatchedDriver = this.state.driver.driverId; //Sometimes signalR is received before API response so need to set driver to be dspatched before making request.
    postData("DispatchDriver", reqParams).then(
      (response) => {
        if (response.status >= 200 && response.status < 400) {
          return;
        }
        this.handleDispatchError(response, isPreDispatch);
      },
      (error) => {
        this.handleDispatchError(error, isPreDispatch);
      }
    );
  };

  handleDispatchError = (response, isPreDispatch) => {
    clearTimeout(this.timer);
    this.dispatchedDriver = null;
    this.setState({
      dispatchInProgress: false,
      dispatchingDelayed: false,
      dispatchError: response?.response ? response?.response.data.error.message : response?.message
    })
    if (response?.response?.status === 403) {
      return;
    }
    this.props.enqueueSnackbar(`Error while ${isPreDispatch ? 'pre-dispatching' : 'dispatching'} driver`, {
      variant: "error",
    });
  };

  isDriverPreSelected = () => {
    return Boolean(this.props.driver?.driverId);
  };

  getSelectedDriverData = (driverId) => {
    const { allAvailableDriverList } = this.props;
    return findDriverById(allAvailableDriverList, driverId) ?? {};
  };

  isDriverNotYetDispatched = (driverId) => {
    const {allAvailableDriverList, dispatchedDrivers } = this.props;
    return (
      !dispatchedDrivers.includes(driverId) &&
      Boolean(findDriverById(allAvailableDriverList, driverId))
    );
  };

  normalizeSafetyData = (safetyData) => {
    if (!Array.isArray(safetyData)) return [];
    return safetyData
      .filter(item => item?.message?.trim?.())
      .map((item) => ({ message: item.message.trim() }));
  };

  getCurrentPreDispatchTrailers = () => this.state.preDispatchTrailers ?? this.props.selectedTrailer ?? [];

  getTrailerDetailsForPreDispatchIdealStart = () =>
    this.state.preDispatchTrailerDetailsForIdealStart ?? this.getCurrentPreDispatchTrailers();

  syncPreDispatchTrailerDetailsForIdealStart = (trailerDetails) => {
    if (!this.props.isPreDispatch) return;
    this.setState((prevState) => {
      const {
        preDispatchIdealStartTimesByTrailer,
      } = resolvePreDispatchIdealStartSync(
        prevState,
        trailerDetails,
        this.props.selectedTrailer
      );
      return {
        preDispatchTrailerDetailsForIdealStart: trailerDetails,
        preDispatchIdealStartTimesByTrailer,
      };
    });
  };

  updatePreDispatchIdealStartTime = (index) => (value) => {
    if (!this.props.isPreDispatch) return;
    this.setState((prevState) => ({
      preDispatchIdealStartTimesByTrailer: updateIdealStartTimesByTrailerAtIndex({
        preDispatchIdealStartTimesByTrailer: prevState.preDispatchIdealStartTimesByTrailer,
        trailerDetails:
          prevState.preDispatchTrailerDetailsForIdealStart ??
          prevState.preDispatchTrailers ??
          this.props.selectedTrailer ??
          [],
        index,
        value,
      }),
    }));
  };

  handleDriverSelection = (value) => {
    const selectedDriverData = this.getSelectedDriverData(value?.value);
    const { driverId, driverName, driverNumber, safetyData } = selectedDriverData;
    if (!driverNumber) return;

    const isPreDispatch = this.props.isPreDispatch;
    const maxAllowed = this.props.DispatchStore?.maxAllowedTrailerCount ?? 0;
    const driverData = isPreDispatch
      ? (this.props.DispatchStore.dispatchedDrivers.find((d) => d.driverId === driverId) ?? null)
      : null;
    const routeCount = driverData?.dispatchRoutes?.length ?? 0;

    const isDriverDispatchedWithST =
      driverData?.dispatchRoutes?.some((route) => route.trailer?.isStraightTruck) ?? false;
    const isPreSelectedTrailerIsST = this.props.selectedTrailer?.length ? this.props.selectedTrailer[0]?.isStraightTruck : false;
    const driverError = getDriverError(
      getDriverErrorArgs(this.getDriverErrorArgsInput(driverId, driverData))
    );

    let selectedTrailer = this.props.selectedTrailer;
    let selectedTractor = this.props.selectedTractor ?? constants.DEFAULT_TRACTOR;
    if (isPreDispatch && driverData?.dispatchRoutes?.length && !isDriverDispatchedWithST && !isPreSelectedTrailerIsST) {
      const preSelectedTrailers =
        driverData.dispatchRoutes.map((route) => getPreSelectedTrailer({route, trailer: route.trailer, isAlreadyDispatchedRoute: true})) || [];
      const atMax = maxAllowed > 0 && routeCount >= maxAllowed;
      selectedTrailer = atMax ? preSelectedTrailers : (selectedTrailer ? [...preSelectedTrailers, ...selectedTrailer] : preSelectedTrailers);
      selectedTractor = driverData?.tractorNumber || constants.DEFAULT_TRACTOR;
    }

    this.setState((prevState) => {
      let sameTrailerRows = null;
      let preDispatchIdealStartTimesByTrailer = null;
      if (isPreDispatch) {
        const result = resolvePreDispatchIdealStartSync(
          prevState,
          selectedTrailer,
          this.props.selectedTrailer
        );
        sameTrailerRows = result.sameTrailerRows;
        preDispatchIdealStartTimesByTrailer = result.preDispatchIdealStartTimesByTrailer;
      }
      return {
        preDispatchTractor: selectedTractor,
        preDispatchTrailers: selectedTrailer,
        preDispatchTrailerDetailsForIdealStart: sameTrailerRows
          ? prevState.preDispatchTrailerDetailsForIdealStart
          : null,
        preDispatchIdealStartTimesByTrailer,
        driver: {
          driverId,
          driverName,
          driverNumber,
          driverError,
          safetyData: this.normalizeSafetyData(safetyData)
        },
        isDriverSafetyOk: false
      };
    });
    this.setDriverImage(driverNumber);
  };

  closeTrailerDefectModal = () => {
    this.setState({
      showEquipementDefect: null,
    });
  };

  openDvirReports = (equipmentType) => () => {
    this.setState({
      showEquipementDefect: equipmentType,
    });
  };

  setDriverImage = async (driverNumber) => {
    if (!driverNumber) {
      return;
    }
    let driverImage = this.state.driversImage[driverNumber];
    if (!driverImage) {
      driverImage = (
        await fetchDriverImage([driverNumber], this.props.terminalCode)
      )[0]?.driverImage;
      this.setState((prevState) => ({
        ...prevState,
        driversImage: {
          ...prevState.driversImage,
          [driverNumber]: driverImage,
        },
      }));
    }
  };

  updateDriverSafetyOk = (e) => {
    this.setState({ isDriverSafetyOk: e.target.checked })
  }

  clearDriverImageCache = () => {
    this.setState({ driversImage: {} });
  }
  

  renderSafteyMessage = () => {
    if (this.state.driver.safetyData?.length) {
      return (
        <SafetyData
          safetyData={this.state.driver.safetyData}
          isOK={this.state.isDriverSafetyOk}
          onIsOKCLick={this.updateDriverSafetyOk}
        />
      );
    }
    return null;
  }

  getDispatchTrailerCommonProps = () => {
    const { driver, isDataLoaded, isDriverSafetyOk } = this.state;
    const {
      trailerStatusConfig = [],
      tractorStatusConfig = [],
      dollyStatusConfig = [],
      driverStatusConfig = []
    } = this.props.DispatchStore.staticClientData?.clientCodes ?? {};
    return {
      isLoading: !isDataLoaded,
      allTrailers: this.trailerDetailsList,
      trailerList: this.trailerList,
      routeList: this.routeList,
      routeNameConfig: this.routeNameConfig,
      dollyList: this.dollyList,
      tractorList: this.tractorList,
      cancel: this.props.onClose,
      dispatchDriver: this.dispatchDriver,
      isPreselectedDriver: this.isDriverPreSelected(),
      openDvirReports: this.openDvirReports,
      disableDispatch: driver.safetyData?.length && !isDriverSafetyOk || driver.driverError?.error,
      trailerStatusConfig,
      tractorStatusConfig,
      dollyStatusConfig,
      driverStatusConfig,
      maxAllowedTrailerCount: this.props.DispatchStore.maxAllowedTrailerCount,
      terminalCode: this.props.terminalCode,
      isDriverEligibleForNormalDispatch: this.isDriverEligibleForNormalDispatch(),
    };
  }

  renderDispatchContent = () => {
    const { selectedDispatchType, driver, isDataLoaded } = this.state;
    const { tractorStatusConfig = [] } = this.props.DispatchStore.staticClientData?.clientCodes ?? {};
    if (selectedDispatchType !== appConstants.DISPATCH_TYPE.TRAILER) {
      return (
        <Bobtail
          isLoading={!isDataLoaded}
          tractorList={this.tractorList}
          routeList={this.routeList}
          cancel={this.props.onClose}
          dispatchDriver={this.dispatchDriver}
          openDvirReports={this.openDvirReports}
          disableDispatch={driver.safetyData?.length && !this.state.isDriverSafetyOk}
          tractorStatusConfig={tractorStatusConfig}
        >
          {this.renderSafteyMessage()}
        </Bobtail>
      );
    }
    if (this.props.isPreDispatch) {
      return (
        <PreDispatchTrailerPanel
          key={getDispatchTrailerKey(this.props.selectedTrailer, this.state.preDispatchTrailers, driver.driverId)}
          {...this.getDispatchTrailerCommonProps()}
          selectedTrailer={this.state.preDispatchTrailers ?? this.props.selectedTrailer}
          selectedTractor={this.state.preDispatchTractor ?? this.props.selectedTractor}
          preDispatchIdealStartTimes={getIdealStartTimesForTrailers({
            preDispatchIdealStartTimesByTrailer: this.state.preDispatchIdealStartTimesByTrailer,
            trailerDetails: this.getTrailerDetailsForPreDispatchIdealStart(),
          })}
          updateIdealStartTime={this.updatePreDispatchIdealStartTime}
          onPreDispatchTrailerDetailsSync={this.syncPreDispatchTrailerDetailsForIdealStart}
          driver={driver}
        >
          {this.renderSafteyMessage()}
        </PreDispatchTrailerPanel>
      );
    }
    return (
      <DispatchTrailer {...this.getDispatchTrailerCommonProps()}>
        {this.renderSafteyMessage()}
      </DispatchTrailer>
    );
  }

  render() {
    const { classes } = this.props;
    const isPreselectedDriver = this.isDriverPreSelected();
    const { selectedDispatchType, driver } = this.state;

    return (
      <div ref={this.container} className={classes.container}>
        <PanelHeader
          isPreselectedDriver={isPreselectedDriver}
          data={driver}
          handleDriverSelection={this.handleDriverSelection}
          availableDriverOptions={this.getDriversList()}
          dispatchInProgress={this.state.dispatchInProgress}
          driverImage={this.state.driversImage[driver.driverNumber]}
        />
        <Divider className={classes.headerDivider} />
        <div className={classes.contentContainer}>
          <Grid container spacing={8} direction="row" className={classes.main}>
            <Grid container>
              <Grid item xs={12} className={classes.headerStyle}>
                DISPATCH WITH
              </Grid>
              <Grid item xs={12} className={classes.radioGroupStyle}>
                <CustomRadioGroup
                  variant="filled"
                  onChange={this.handleRadioChange}
                  labels={[
                    {
                      value: appConstants.DISPATCH_TYPE.TRAILER,
                      labelText: "Trailer",
                    },
                    {
                      value: appConstants.DISPATCH_TYPE.BOBTAIL,
                      labelText: "Bob Tail",
                      disabled: this.props.isPreDispatch,
                    },
                  ]}
                  defaultValue={selectedDispatchType}
                />
              </Grid>
              {this.state.dispatchError ? (
                <DispatchError message={this.state.dispatchError} />
              ) : null}
            </Grid>
          </Grid>

          {this.renderDispatchContent()}

          {this.state.dispatchInProgress ? (
            <DispatchInProgress
              close={this.props.onClose}
              delayed={this.state.dispatchingDelayed}
            />
          ) : null}
          {this.state.showEquipementDefect && (
            <EquipmentDefects
              userProfile={this.props.userProfile}
              onCancel={this.closeTrailerDefectModal}
              defaultEquipmentType={this.state.showEquipementDefect}
            />
          )}
        </div>
      </div>
    );
  }
}

// Create a wrapper component that handles the container and error boundary
const DispatchPanelWithErrorBoundary = (props) => (
  <ErrorBoundary onClose={props.onClose}>
    <DispatchPanel {...props} />
  </ErrorBoundary>
);

export default compose(
  WithDrawer,
  withStyles(styles),
  withSnackbar,
  withAppConfig,
)(DispatchPanelWithErrorBoundary);
